import * as React from 'react';
import { resumeCheckout, restartCheckout } from "../loaders";

type Props = Readonly<{
	hash: string,
	setState: Function,
	memberLoginUrl?: string | null,
}>;

/*
	The locked overlay shown over the details / start-date form once the checkout is
	committed — a Direct Debit mandate or a payable object exists on Resamania's
	side, which fixes both the member's details and the plan's start date. They
	cannot be edited from here without tearing those down.

	Rather than disabling each field (which greys them unevenly — the phone input
	especially), the form beneath is made `inert` and this lays a translucent sheet
	over it so the whole thing reads as one faded, read-only record. The card is
	sticky, so the way out stays in view however far down the form the customer has
	scrolled:

	  - carry on with the checkout where they left off,
	  - start again from scratch with a different email address, or
	  - finish in the Resamania members' portal.

	"Start again" replaces the old behaviour, where the only way out was to clear the
	browser's storage by hand. The parent must be positioned (relative) for the
	`absolute inset-0` sheet to cover the form.
*/
function LockedNotice({hash, setState, memberLoginUrl}: Props) {
	return (
		<div className="absolute inset-0 flex justify-center bg-white/70">
			<div className="sticky top-4 self-start w-full max-w-xl">
				<div className="bg-grey-100 border border-grey-400 shadow-xl p-4 sm:p-6">
					<h5 className="mb-2">YOUR MEMBERSHIP IS BEING SET UP</h5>
					<p className="text-sm mb-6">
						Your details and start date are locked in now, so they can’t be
						changed here. You can carry on with your checkout, or start again with
						a different email address.
					</p>

					{/* Two equal, primary choices sharing the width. */}
					<div className="flex flex-col sm:flex-row gap-3">
						<button
							type="button"
							className="!mb-0 flex-1 flex items-center justify-center"
							onClick={() => resumeCheckout(hash, setState)}
						>
							CONTINUE CHECKOUT
						</button>
						<button
							type="button"
							className="grey !mb-0 flex-1"
							onClick={restartCheckout}
						>
							START AGAIN
						</button>
					</div>

					{/* Tertiary affordance: keep the same email by editing in the portal. */}
					{memberLoginUrl &&
						<p className="text-xs text-grey-600 text-center mt-5 mb-0">
							Need to change your plan and keep the same email address?{' '}
							<a href={memberLoginUrl} className="underline">Log in to your account</a>{' '}
							to set it up.
						</p>
					}
				</div>
			</div>
		</div>
	);
}

export default LockedNotice;
